<template>
  <Teleport to="body">
    <Transition name="slide-fade">
      <v-card
        v-if="modelValue"
        class="notification-popup relative"
        elevation="4"
        @click="handleView"
      >
        <v-card-text class="notification-content">
          <div class="content-wrapper">
            <v-icon color="primary" size="small" class="view-icon">mdi-eye-outline</v-icon>
            <div class="message-section">
              <div class="text-body-2 line-height-2 mb-2">
                {{ shortMessage }}
              </div>
              <div class="text-grey text-caption">
                {{ formattedDate }}
              </div>
            </div>
            <v-btn
              id="notification-mark-read-btn"
              color="primary"
              icon="mdi-bell-outline"
              variant="text"
              size="small"
              class="mark-read-btn"
              @click.stop="handleClose"
            />
            <v-tooltip activator="#notification-mark-read-btn" location="top">
              Отметить прочитанным
            </v-tooltip>
          </div>
        </v-card-text>
      </v-card>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import moment from 'moment'

moment.locale('ru')

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: '',
  },
  date: {
    type: String,
    default: '',
  },
  notificationId: {
    type: [String, Number],
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'close', 'view'])

const shortMessage = computed(() => {
  if (!props.message) return ''
  return props.message.length > 100 ? `${props.message.substr(0, 100)}...` : props.message
})

const formattedDate = computed(() => {
  if (!props.date) return ''

  const dateStr = String(props.date).trim()
  if (!dateStr) return ''

  let dateMoment = moment(dateStr)

  if (!dateMoment.isValid()) {
    const formats = [
      'DD.MM.YYYY HH:mm:ss',
      'DD.MM.YYYY HH:mm',
      'DD.MM.YYYY',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD',
    ]
    dateMoment = moment(dateStr, formats, true)
  }

  if (!dateMoment.isValid()) {
    return dateStr
  }

  const now = moment()
  const diffInMinutes = now.diff(dateMoment, 'minutes')
  const diffInHours = now.diff(dateMoment, 'hours')
  const diffInDays = now.diff(dateMoment, 'days')

  if (diffInMinutes < 1) {
    return 'только что'
  } else if (diffInMinutes < 60) {
    return dateMoment.fromNow()
  } else if (diffInHours < 24 && now.date() === dateMoment.date()) {
    return dateMoment.format('HH:mm')
  } else if (diffInDays === 1 || (diffInDays < 2 && now.date() === dateMoment.date() + 1)) {
    return 'Вчера, ' + dateMoment.format('HH:mm')
  } else if (diffInDays < 7) {
    return dateMoment.format('dddd, HH:mm')
  } else {
    return dateMoment.format('DD.MM.YYYY, HH:mm')
  }
})

function handleClose() {
  emit('close', props.notificationId)
  emit('update:modelValue', false)
}

function handleView() {
  emit('view', {
    id: props.notificationId,
    message: props.message,
    date: props.date,
  })
  emit('update:modelValue', false)
}
</script>

<style>
.notification-popup {
  position: fixed !important;
  top: 80px !important;
  right: 24px !important;
  width: 340px !important;
  max-width: calc(100vw - 48px) !important;
  z-index: 1000 !important;
  background: white !important;
  border-radius: 8px !important;
  overflow: hidden !important;
  border: 2px solid #ff9800 !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

.notification-popup:hover {
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3) !important;
  transform: translateY(-2px) !important;
}

.notification-content {
  padding: 16px !important;
}

.content-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.view-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.mark-read-btn {
  flex-shrink: 0;
  margin-top: 2px;
}

.message-section {
  flex: 1;
  min-width: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>

